import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageCircle } from "lucide-react";
import { MaskLine, Reveal, FadeIn, Overline } from "@/components/motion";
import { Marquee, SynapseMark } from "@/components/Layout";
import { WA_URL } from "@/lib/site";

const HOME_EXAMS = [
  {
    num: "01",
    badge: "Referto a fine esame",
    name: "Elettromiografia (EMG)",
    text: "Formicolii o bruciori alle mani, soprattutto di notte. Dolore dal collo o dalla schiena che scende lungo braccio o gamba. Guizzi muscolari che non passano. L'elettromiografia misura come funzionano nervi e muscoli.",
  },
  {
    num: "02",
    badge: "Referto in 24–48 ore",
    name: "Elettroencefalogramma (EEG)",
    text: "Momenti in cui ci si «incanta» e si perde il contatto con ciò che accade. Scosse o perdite di coscienza improvvise. Episodi di confusione in un genitore anziano. L'elettroencefalogramma registra l'attività elettrica del cervello.",
  },
  {
    num: "03",
    badge: "Referto in 7 giorni",
    name: "Poligrafia del sonno",
    text: "Otto ore di sonno, e al mattino la stanchezza è già lì. Russamento, o pause nel respiro che nota chi dorme accanto a te. La poligrafia registra respiro, ossigeno e battito durante la notte, e si fa a casa tua.",
  },
  {
    num: "04",
    badge: "Referto in 14 giorni",
    name: "Polisonnografia",
    text: "Insonnia che dura da mesi. Movimenti, parole o urla durante la notte, che al mattino non ricordi. È lo studio completo del sonno: registra anche attività del cervello, movimenti e fasi del riposo. Una notte, a casa tua.",
  },
];

const METODO = [
  {
    num: "01",
    title: "Ti ascoltiamo",
    text: "Racconti tutta la tua storia, senza fretta. Ti seguiamo anche quando il problema non ha una soluzione semplice.",
  },
  {
    num: "02",
    title: "Ci confrontiamo per te",
    text: "Nel team ci sono specialisti formati all'Ospedale Molinette di Torino. Se il tuo caso lo richiede, chiediamo il parere di un collega esperto.",
    highlight: true,
  },
  {
    num: "03",
    title: "Ci trovi anche dopo",
    text: "Dopo l'esame non resti solo con un referto in mano. Puoi tornare da noi con i tuoi dubbi, anche nel tempo.",
  },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCircles = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center bg-indaco-deep overflow-hidden" data-testid="hero">
      {/* Synapse circles parallax background */}
      <motion.div style={{ y: yCircles }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -right-32 top-24 w-[520px] h-[520px] rounded-full border border-crema/10" />
        <div className="absolute -right-10 top-48 w-[520px] h-[520px] rounded-full border border-arancio/25" />
        <div className="absolute right-40 top-72 w-72 h-72 rounded-full bg-arancio/20 blur-[110px] orb-drift" />
        <div className="absolute -left-40 bottom-0 w-[420px] h-[420px] rounded-full border border-indaco/40" />
        <div className="absolute left-24 bottom-24 w-56 h-56 rounded-full bg-indaco/30 blur-[90px] orb-drift" style={{ animationDelay: "-7s" }} />
      </motion.div>

      <motion.div style={{ y: yText, opacity }} className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 w-full">
        <FadeIn delay={0.2}>
          <div className="flex items-center gap-4 mb-10">
            <img src="/logo-synapses.png" alt="Logo Synapses" className="h-10 w-auto" />
            <p className="text-sm uppercase tracking-[0.22em] text-crema/60">
              Centro specialistico in Neurologia e Medicina del Sonno · Torino
            </p>
          </div>
        </FadeIn>

        <h1 className="font-sora font-extrabold tracking-tighter text-crema text-[28px] md:text-5xl lg:text-7xl leading-[1.02]" data-testid="hero-title">
          <MaskLine delay={0.35}>Da quanto tempo ti chiedi</MaskLine>
          <MaskLine delay={0.5}>
            <span className="text-arancio">cosa ti sta succedendo?</span>
          </MaskLine>
        </h1>

        <FadeIn delay={1.1}>
          <p className="mt-10 max-w-xl text-crema/70 text-[17px] md:text-lg md:text-lg leading-relaxed">
            Formicolii, guizzi muscolari, momenti di assenza, notti che non riposano.
            Ti dedichiamo il tempo che serve, poi cerchiamo la risposta con l'esame più adatto.
          </p>
        </FadeIn>

        <FadeIn delay={1.3}>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link
              to="/prenota"
              className="group inline-flex items-center gap-3 rounded-full bg-crema px-8 py-4 text-[15px] md:text-base font-semibold text-ink transition-colors duration-300 hover:bg-arancio hover:text-crema"
              data-testid="hero-cta-prenota"
            >
              Richiedi un appuntamento
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-crema/30 px-8 py-4 text-[15px] md:text-base font-semibold text-crema transition-colors duration-300 hover:border-crema hover:bg-crema/5"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              Chiedi informazioni su WhatsApp
            </a>
          </div>
        </FadeIn>
      </motion.div>

      <FadeIn delay={1.8} className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="w-px h-14 bg-crema/25 overflow-hidden">
          <motion.div
            className="w-px h-6 bg-arancio"
            animate={{ y: [0, 56] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </FadeIn>
    </section>
  );
}

function Perche() {
  return (
    <section className="bg-crema py-28 lg:py-40" data-testid="perche-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal>
            <Overline>Perché esiste Synapses</Overline>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <Reveal>
            <h2 className="font-sora font-bold tracking-tighter text-4xl md:text-5xl leading-tight text-ink">
              Quindici minuti non bastano
              <br />
              per raccontare il tuo sintomo.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-lg text-viole leading-relaxed max-w-2xl">
              Il paziente neurologico ha bisogno prima di tempo e attenzione, poi di esami e soluzioni.
              Synapses è nato per invertire quest'ordine. Qui la visita dura il tempo giusto per
              ascoltarti davvero: non lo decide l'agenda, lo decide la tua storia.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <Link
              to="/il-centro"
              className="mt-10 inline-flex items-center gap-2 text-indaco font-semibold text-[15px] md:text-base group"
              data-testid="perche-cta"
            >
              Leggi come è nato Synapses
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Pilastri() {
  return (
    <section className="bg-indaco-deep text-crema py-28 lg:py-40" data-testid="pilastri-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline light>Il metodo</Overline>
        </Reveal>
        <div className="mt-14 divide-y divide-crema/10">
          {METODO.map((p, i) => (
            <Reveal key={p.num} delay={0.1 * i}>
              <div className="group grid md:grid-cols-12 gap-6 py-12 md:py-16 items-start">
                <span className="md:col-span-2 font-sora text-[15px] md:text-base text-arancio font-semibold tracking-widest">
                  {p.num}
                </span>
                <h3
                  className={`md:col-span-4 font-sora font-bold tracking-tighter text-3xl md:text-5xl transition-colors duration-300 ${
                    p.highlight ? "text-arancio" : "group-hover:text-arancio"
                  }`}
                >
                  {p.title}
                </h3>
                <p className="md:col-span-6 text-crema/65 leading-relaxed text-[17px] md:text-lg md:text-lg max-w-xl">
                  {p.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function EsamiPreview() {
  return (
    <section className="bg-crema-warm py-28 lg:py-40" data-testid="esami-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <Reveal>
              <Overline>Prestazioni ed esami</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-sora font-bold tracking-tighter text-4xl md:text-5xl text-ink max-w-2xl">
                Quale esame fa per te? Parti da ciò che senti.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/prestazioni"
              className="inline-flex items-center gap-2 text-indaco font-semibold text-[15px] md:text-base group shrink-0"
              data-testid="esami-cta"
            >
              Tutte le prestazioni
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {HOME_EXAMS.map((e, i) => (
            <Reveal key={e.num} delay={0.08 * i}>
              <Link
                to="/prestazioni"
                className="group block bg-crema rounded-lg p-8 lg:p-10 border border-ink/5 transition-colors duration-300 hover:border-indaco/30 h-full"
                data-testid={`exam-card-${e.num}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="font-sora text-sm font-semibold tracking-widest text-viole">{e.num}</span>
                  <span className="inline-flex items-center rounded-full border border-arancio/40 px-3 py-1 text-sm font-semibold uppercase tracking-wider text-arancio">
                    {e.badge}
                  </span>
                </div>
                <h3 className="mt-8 font-sora font-bold tracking-tight text-2xl text-ink break-words transition-colors duration-300 group-hover:text-indaco">
                  {e.name}
                </h3>
                <p className="mt-3 text-viole text-[15px] md:text-base leading-relaxed">{e.text}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-indaco opacity-0 translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                  Scopri <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const HOME_QUOTES = [
  {
    text: "Molto esaustivo e competente a valutare un quadro completo dedicando tutto il tempo necessario al paziente. Trasmette tranquillità e mette a proprio agio.",
    meta: "S.F. · Prima visita neurologica presso Synapses · luglio 2026",
  },
  {
    text: "Dottore empatico, calmo, gentile e disponibile ad ascoltare le problematiche senza metter fretta ai pazienti. Molto chiaro nel prescrivere le cure e nelle spiegazioni.",
    meta: "F.A. · Prima visita neurologica presso Synapses · aprile 2026",
  },
  {
    text: "Dottore molto professionale, delicato durante l'esame e ottimo studio. Ha saputo calmarmi durante l'EMG per la mia paura degli aghi. Molto gentile",
    meta: "G.C. · Elettromiografia · settembre 2026",
  },
  {
    text: "Il Dott. Cavallo è estremamente competente e attento ai dettagli. Ha esaminato il mio caso senza pregiudizi, anche di fronte a una condizione rara in età giovane. Un vero professionista, affidabile e serio.",
    meta: "F.I. · Prima visita neurologica · febbraio 2025",
  },
];

function Recensioni() {
  return (
    <section className="bg-crema py-28 lg:py-40" data-testid="recensioni-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline>Cosa dicono i pazienti</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-sora font-bold tracking-tighter text-4xl md:text-5xl text-ink max-w-3xl">
            Il tempo dedicato è la cosa che notano per prima.
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 text-viole text-lg leading-relaxed max-w-2xl">
            81 recensioni verificate del Dott. Enrico Cavallo su MioDottore, raccolte a Synapses
            e nelle altre strutture in cui lavora.
          </p>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="mt-5 flex items-center gap-2 text-[15px] md:text-base font-semibold text-ink" data-testid="home-score">
            5,0 <span className="text-arancio text-[17px] md:text-lg leading-none" aria-hidden="true">★</span> su MioDottore · 81 recensioni verificate
          </p>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-2 gap-5">
          {HOME_QUOTES.map((q, i) => (
            <Reveal key={i} delay={0.06 * i}>
              <blockquote className="bg-crema-warm rounded-lg border border-ink/5 p-8 lg:p-10 h-full flex flex-col" data-testid={`home-quote-${i}`}>
                <p className="text-ink/85 leading-relaxed text-lg font-sora font-medium tracking-tight">
                  «{q.text}»
                </p>
                <footer className="mt-6 text-sm text-viole">{q.meta}</footer>
              </blockquote>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-4">
            <Link
              to="/recensioni"
              className="group inline-flex items-center gap-2 text-indaco font-semibold text-[15px] md:text-base"
              data-testid="home-recensioni-link"
            >
              Leggi le recensioni dei pazienti
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CtaFinale() {
  return (
    <section className="relative bg-indaco-deep text-crema py-32 lg:py-44 overflow-hidden" data-testid="cta-finale">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full border border-crema/10" />
        <div className="absolute left-[55%] top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full border border-arancio/20" />
      </div>
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <SynapseMark className="w-16 h-12 mx-auto mb-10" indigo="#FAF6F1" />
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-sora font-bold tracking-tighter text-4xl md:text-6xl leading-tight">
            Hai un sintomo che
            <br />
            non ti spieghi?
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 text-crema/70 text-lg max-w-xl mx-auto leading-relaxed">
            Raccontacelo, anche in due righe. Ti proponiamo noi un orario entro 24 ore, senza impegno.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <Link
              to="/prenota"
              className="group inline-flex items-center gap-3 rounded-full bg-crema px-8 py-4 text-[15px] md:text-base font-semibold text-ink transition-colors duration-300 hover:bg-arancio hover:text-crema"
              data-testid="cta-finale-prenota"
            >
              Richiedi un appuntamento
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-crema/30 px-8 py-4 text-[15px] md:text-base font-semibold text-crema transition-colors duration-300 hover:border-crema"
              data-testid="cta-finale-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              Chiedi informazioni su WhatsApp
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <div className="bg-indaco-deep border-t border-crema/10">
        <Marquee light items={["5,0 ★ su 81 recensioni verificate", "Non serve l'impegnativa", "Risposta entro 24 ore", "Specialisti formati all'Ospedale Molinette"]} />
      </div>
      <Perche />
      <EsamiPreview />
      <Pilastri />
      <Recensioni />
      <CtaFinale />
    </>
  );
}
